import React from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import dynamic from 'next/dynamic';

import clsx from 'clsx';

import { useFeatures } from '@atc/features';

import { EmailCTA } from 'reaxl-email';
import { OwnerPhoneNumber, OwnerTextCTA } from 'reaxl-molecules';
import { Payment } from 'reaxl-wallet';

import paymentsDuck from '@/ducks/paymentsDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import useDealerTextMessage from '@/hooks/useDealerTextMessage';

import LazyComponent from '@/components/LazyComponent';
import CalculatorJumpLink from '@/components/vehicledetails/CalculatorJumpLink';

const PsxButton = dynamic(() => import(/* webpackChunkName: "PsxButton" */ '../psx/PsxButton'), { ssr: false });

function renderContactCTAs({
    analyticsData,
    buttonProps,
    isTextCTAEnabled,
    phone,
    phoneClickHandler,
    textBody,
    textNumber,
}) {
    const contactCTAs = [];

    if (phone?.value) {
        contactCTAs.push(
            <OwnerPhoneNumber
                key="phone-cta"
                analyticsData={analyticsData}
                phone={phone}
                phoneClickHandler={phoneClickHandler}
                uiContext="button"
                buttonLabel="Call"
                {...buttonProps}
            />
        );
    }

    if (textNumber && isTextCTAEnabled) {
        contactCTAs.push(
            <OwnerTextCTA
                analyticsData={analyticsData}
                buttonBsStyle="secondary"
                textClickHandler={phoneClickHandler}
                key="text-cta"
                label="Text"
                phoneNumber={textNumber}
                uiContext="button"
                textBody={textBody}
                {...buttonProps}
            />
        );
    }

    return contactCTAs;
}

function MediaGalleryCTAs({
    disableContactCTAs,
    emailCTAProps,
    handleCloseMediaGalleryModal,
    handleNDSCTAClick,
    phoneClickHandler,
    relocateCTAs,
    seeDetailsLabel,
}) {
    const {
        nds_calc: [isNDSCalcEnabled],
        vdp_sticky_header: [, {
            buttonSize: stickyBtnSize,
            buttonStyle: stickyBtnStyle,
            style: stickyStyle,
        }],
        media_gallery_text_cta: [isTextCTAEnabled],
    } = useFeatures([
        'nds_calc',
        'vdp_sticky_header',
        'media_gallery_text_cta',
    ]);

    const textBody = useDealerTextMessage();
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};
    const { id: inventoryId, owner = {}, paymentServices: { dealerSettings: { leaseEnabled } = {} } = {} } = listing || {};

    const paymentEmphasis = useSelector((state) => paymentsDuck.selectors.getMyWalletInteraction(state, 'paymentEmphasis'));
    const financePayment = useSelector((state) => paymentsDuck.selectors.getLowestPencilingPaymentByListingId(state, inventoryId)?.payment);
    const listingLeasePayments = useSelector((state) => paymentsDuck.selectors.getListingLeasePayments(state)) || {};
    const paymentOption = useSelector((state) => paymentsDuck.selectors.getPaymentOption(state));
    const leasePayment = leaseEnabled && paymentOption === 'lease' && listingLeasePayments[inventoryId]?.payment;

    const {
        hideEmailForm,
        id: ownerId,
        phone,
        privateSeller,
        textNumber,
    } = owner;

    const showPayment = paymentEmphasis && (leasePayment || financePayment);
    const useSmallButton = relocateCTAs && stickyBtnSize === 'small' || !relocateCTAs;
    const analyticsData = {
        inventoryId,
        ownerId,
        placement: 'media-gallery',
    };
    const ctas = [];
    const renderLabel = showPayment
        ? (
            <>
                <Payment
                    uiContext="button"
                    payment={leasePayment || financePayment}
                />
                &nbsp;
                {seeDetailsLabel}
            </>
        ) : 'Calculate Payment';

    if (privateSeller) {
        ctas.push(
            <LazyComponent key="psx-media-gallery-ctas">
                <div className="display-flex gap-2">
                    <PsxButton
                        buttonSize="small"
                        uiContext="makeOffer"
                        onClick={handleCloseMediaGalleryModal}
                    >
                        Make Offer
                    </PsxButton>
                    <PsxButton
                        buttonSize="small"
                        buttonStyle="secondary"
                        uiContext="chat"
                        onClick={handleCloseMediaGalleryModal}
                    >
                        Chat Seller
                    </PsxButton>
                    <PsxButton
                        buttonSize="small"
                        buttonStyle="secondary"
                        uiContext="testDrive"
                        onClick={handleCloseMediaGalleryModal}
                    >
                        Test Drive
                    </PsxButton>
                </div>
            </LazyComponent>
        );
    }

    if (isNDSCalcEnabled && !privateSeller && !relocateCTAs) {
        ctas.push(
            <CalculatorJumpLink
                key="calculate-payment"
                label={renderLabel}
                bsStyle="secondary"
                bsSize="small"
                onClick={(event) => handleNDSCTAClick(event, showPayment ? 'See Details' : 'Calculate Payment', 'top')}
            />
        );
    }
    const buttonProps = useSmallButton ? { bsSize: 'small' } : {};
    const buttonStyle = relocateCTAs ? stickyBtnStyle : 'primary';
    const type = relocateCTAs ? 'block' : 'button';

    if (relocateCTAs && !disableContactCTAs) {
        ctas.push(
            ...renderContactCTAs({
                analyticsData,
                buttonProps,
                isTextCTAEnabled,
                phone,
                phoneClickHandler,
                textBody,
                textNumber,
            })
        );
    }

    if (!(privateSeller || hideEmailForm)) {
        ctas.push(
            <EmailCTA
                key="email-cta"
                {...emailCTAProps}
                showIcon={false}
                buttonStyle={buttonStyle}
                {...(useSmallButton && { buttonSize: 'small' })}
                context="mediaGallery"
                labelKey="checkAvailability"
                className={clsx({ 'flex-grow': relocateCTAs })}
                type={type}
            />
        );
    }

    return (
        <div
            className={clsx({
                'flex-column flex-sm-row margin-left-auto media-gallery-heading-ctas align-items-end gap-1': !relocateCTAs,
                'gap-3 padding-vertical-3 padding-horizontal-4': relocateCTAs,
                [stickyStyle]: relocateCTAs,
            }, 'display-flex')}
            key="media-gallery-ctas"
        >
            {ctas}
        </div>
    );
}

MediaGalleryCTAs.propTypes = {
    /**
     * Boolean to disable phone/chat/email for the Hyundai experience
     */
    disableContactCTAs: PropTypes.bool,
    /**
     * Function to close the media gallery modal when a CTA button is clicked.
     */
    handleCloseMediaGalleryModal: PropTypes.func,
};

export default MediaGalleryCTAs;
